import React, { FC, useCallback, useMemo } from 'react';

import PageBasic from '../../../components/display/layout/pageBasic';
import LoadingSpinner from '../../../components/loading/nestedSpinner';
import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';

//  hooks
import useFormulasCatalogQuery from '../../../hooks/formulas/useFormulasCatalogQuery';

// models
import {
    ApprovedFormulaCatalog,
    FormulaBaseModel,
    FormulaCategory,
    FormulaTypesData,
} from '../../../models/formula.model';

import DraftMessagesMain from './DraftMessagesMain';
import LiveMessagesMain from './LiveMessagesMain';
import { processData } from './MessageTableData';
import MessagesFrequencyAccount from './MessagesFrequencyAccount/MessagesFrequencyAccount';

const MessagesMain: FC<React.PropsWithChildren<unknown>> = () => {
    const { data: activedata, isLoading: isActiveLoading } =
        useFormulasCatalogQuery(FormulaCategory.Live);
    const { data: inactivedata, isLoading: isInactiveLoading } =
        useFormulasCatalogQuery(FormulaCategory.Draft);

    const tableLiveData = useCallback(() => {
        const liveData = activedata;
        return liveData ? processData(liveData) : [];
    }, [activedata]);

    const tableDraftData = useCallback(() => {
        const draftData = inactivedata;
        return draftData ? processData(draftData, { isDraft: true }) : [];
    }, [inactivedata]);

    const totalLiveMessages = useMemo(() => {
        const liveData = tableLiveData();
        if (Array.isArray(liveData)) {
            return liveData.reduce(
                (acc: number, rowData) => acc + (rowData?.messages || 0),
                0,
            );
        }

        return 0;
    }, [tableLiveData]);

    const totalDraftMessages = useMemo(() => {
        const draftData = tableDraftData();
        if (Array.isArray(draftData)) {
            return draftData.reduce(
                (acc: number, rowData) => acc + (rowData?.messages || 0),
                0,
            );
        }

        return 0;
    }, [tableDraftData]);

    const calculatePercentages = (
        tableData: ApprovedFormulaCatalog[] | undefined | FormulaBaseModel[],
        totalMessages: number,
    ) => {
        if (!tableData || totalMessages === 0) {
            return [];
        }
        const channelMessageCounts: Record<string, number> = {};

        tableData.forEach((rowData) => {
            const channel = rowData.message_type;

            channelMessageCounts[channel] =
                (channelMessageCounts[channel] || 0) + 1;
        });
        return Object.entries(channelMessageCounts).map(([channel, count]) => {
            const formulaTypeData =
                FormulaTypesData[channel as keyof typeof FormulaTypesData];

            return {
                channel,
                percentage: (count / totalMessages) * 100,
                color: formulaTypeData?.color || '',
            };
        });
    };

    const calculatePercentagesLive = () => {
        return calculatePercentages(activedata, totalLiveMessages);
    };

    const calculatePercentagesDraft = () => {
        return calculatePercentages(inactivedata, totalDraftMessages);
    };

    if (isActiveLoading || isInactiveLoading) {
        return <LoadingSpinner />;
    }

    return (
        <PageBasic>
            <Container>
                <MessagesOverview>
                    <LiveMessagesMain
                        heading={'Live messages'}
                        tableData={tableLiveData}
                        totalMessages={totalLiveMessages}
                        calculatePercentages={calculatePercentagesLive}
                    />
                    <DraftMessagesMain
                        heading={'Drafts'}
                        tableData={tableDraftData}
                        totalMessages={totalDraftMessages}
                        calculatePercentages={calculatePercentagesDraft}
                    />
                </MessagesOverview>
                <MessagesFrequencyAccount />
            </Container>
        </PageBasic>
    );
};

export default MessagesMain;

const Container = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing(3),
});
const MessagesOverview = styled.div({
    display: 'flex',
    flexDirection: 'column',
    width: '70%',
});
